import React, { useRef, useState } from "react";

import { useHistory } from "react-router-dom";

import {
  Button,
  Grid,
  Paper,
  Tab,
  Tabs,
  Box,
} from "@material-ui/core";
import {
  Add as AddIcon,
  Forum as ForumIcon,
  Message as MessageIcon,
} from "@material-ui/icons";
import ChatList from "./ChatList";
import ChatMessages from "./ChatMessages";

import { has, isObject } from "lodash";

import withWidth, { isWidthUp } from "@material-ui/core/withWidth";

import { useStyles } from "./chatStyles";
import { ChatModal } from "./components/ChatModal";
import ChatWelcomeScreen from "./components/ChatWelcomeScreen";
import EmptyMessagesPlaceholder from "./components/EmptyMessagesPlaceholder";
import useChatPage from "./hooks/useChatPage";

function Chat(props) {
  const classes = useStyles();
  const history = useHistory();
  const scrollToBottomRef = useRef();

  const [showDialog, setShowDialog] = useState(false);
  const [dialogType, setDialogType] = useState("new");
  const [tab, setTab] = useState(0);

  const {
    chats,
    chatsPageInfo,
    messages,
    messagesPageInfo,
    loading,
    currentChat,
    selectChat,
    sendMessage,
    deleteChat,
    loadMoreMessages,
    afterChatCreated,
  } = useChatPage({
    scrollToBottomRef,
    onTabSwitch: setTab,
    onNavigate: (path) => history.push(path),
  });

  const renderGrid = () => {
    // ✅ MOSTRAR PANTALLA DE BIENVENIDA SI NO HAY CONVERSACIONES
    if (chats.length === 0 && !loading) {
      return <ChatWelcomeScreen onNewChat={() => { setDialogType("new"); setShowDialog(true); }} />;
    }

    return (
      <Grid className={classes.gridContainer} container>
        <Grid className={`${classes.gridItem} ${classes.chatColumn}`} item xs={12} md={3}>
          <div className={classes.btnContainer}>
            <Button
              onClick={() => {
                setDialogType("new");
                setShowDialog(true);
              }}
              color="primary"
              variant="contained"
              startIcon={<AddIcon />}
              size="large"
              style={{
                borderRadius: 8,
                textTransform: 'none',
                fontWeight: 600,
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                }
              }}
            >
              Nueva Conversación
            </Button>
          </div>

          <ChatList
            chats={chats}
            pageInfo={chatsPageInfo}
            loading={loading}
            handleSelectChat={(chat) => selectChat(chat)}
            handleDeleteChat={(chat) => deleteChat(chat)}
            handleEditChat={() => {
              setDialogType("edit");
              setShowDialog(true);
            }}
          />
        </Grid>
        <Grid className={`${classes.gridItem} ${classes.messagesColumn}`} item xs={12} md={9}>
          {isObject(currentChat) && has(currentChat, "id") ? (
            <ChatMessages
              chat={currentChat}
              scrollToBottomRef={scrollToBottomRef}
              pageInfo={messagesPageInfo}
              messages={messages}
              loading={loading}
              handleSendMessage={sendMessage}
              handleLoadMore={loadMoreMessages}
            />
          ) : (
            <EmptyMessagesPlaceholder />
          )}
        </Grid>
      </Grid>
    );
  };

  const renderTab = () => {
    // ✅ MOSTRAR PANTALLA DE BIENVENIDA SI NO HAY CONVERSACIONES EN MÓVIL
    if (chats.length === 0 && !loading) {
      return <ChatWelcomeScreen onNewChat={() => { setDialogType("new"); setShowDialog(true); }} />;
    }

    return (
      <Grid className={`${classes.gridContainer} ${classes.tabLayoutRoot}`} container direction="column">
        <Grid item xs={12} style={{ flexShrink: 0 }}>
          <Tabs
            value={tab}
            indicatorColor="primary"
            textColor="primary"
            onChange={(e, v) => setTab(v)}
            aria-label="chat tabs"
            variant="scrollable"
            scrollButtons="auto"
          >
            <Tab
              label="Conversaciones"
              icon={<ForumIcon />}
            />
            <Tab
              label="Mensajes"
              icon={<MessageIcon />}
              disabled={!isObject(currentChat) || !has(currentChat, "id")}
            />
          </Tabs>
        </Grid>

        {tab === 0 && (
          <Grid className={classes.gridItemTab} item xs={12}>
            <div className={classes.btnContainer}>
              <Button
                onClick={() => {
                  setDialogType("new");
                  setShowDialog(true);
                }}
                color="primary"
                variant="contained"
                startIcon={<AddIcon />}
                fullWidth
                size="large"
                style={{
                  borderRadius: 8,
                  textTransform: 'none',
                  fontWeight: 600,
                  boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: 'translateY(-2px)',
                    boxShadow: '0 6px 16px rgba(0,0,0,0.2)',
                  }
                }}
              >
                Nueva Conversación
              </Button>
            </div>
            <ChatList
              chats={chats}
              pageInfo={chatsPageInfo}
              loading={loading}
              handleSelectChat={(chat) => selectChat(chat)}
              handleDeleteChat={(chat) => deleteChat(chat)}
            />
          </Grid>
        )}

        {tab === 1 && (
          <Grid className={classes.gridItemTab} item xs={12}>
            {isObject(currentChat) && has(currentChat, "id") ? (
              <ChatMessages
                chat={currentChat}
                scrollToBottomRef={scrollToBottomRef}
                pageInfo={messagesPageInfo}
                messages={messages}
                loading={loading}
                handleSendMessage={sendMessage}
                handleLoadMore={loadMoreMessages}
              />
            ) : (
              <EmptyMessagesPlaceholder mobile />
            )}
          </Grid>
        )}
      </Grid>
    );
  };

  /** Mientras no hay chats (bienvenida o carga): Paper con scroll; con lista fija altura a columnas */
  const useScrollableChatPanel = chats.length === 0;

  return (
    <>
      <ChatModal
        type={dialogType}
        open={showDialog}
        chat={currentChat}
        handleLoadNewChat={(data) => {
          afterChatCreated(data);
          setTab(1);
          history.push(`/chats/${data.uuid}`);
        }}
        handleClose={() => setShowDialog(false)}
      />
      <Box className={classes.chatPageOuter}>
        <Paper
          className={`${classes.mainContainer} ${
            useScrollableChatPanel ? classes.mainContainerWelcome : classes.mainContainerWithChats
          }`}
        >
          {isWidthUp("md", props.width) ? renderGrid() : renderTab()}
        </Paper>
      </Box>
    </>
  );
}

export { ChatModal } from "./components/ChatModal";
export default withWidth()(Chat);
